import { useState, useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Plus,
  Minus,
  Loader2,
  Save,
  Eye,
  Pencil,
  ImagePlus,
  X,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { getCategories, uploadImageToS3 } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { createErrorMessage } from "@/lib/error-message";
import type { Category } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Markdown } from "@/components/ui/markdown";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_main/exams/$slug/admin/questions/new")({
  component: AdminNewQuestionPage,
});

const API_BASE =
  import.meta.env.VITE_API_URL || "http://localhost:8080/api/v1";

function adminFetch(endpoint: string, options: RequestInit = {}) {
  const token = getToken();
  if (!token) {
    return Promise.reject(new Error("로그인이 필요합니다"));
  }
  return fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...((options.headers as Record<string, string>) ?? {}),
    },
  });
}

interface ChoiceInput {
  label: string;
  text: string;
  is_correct: boolean;
}

const LABELS = ["A", "B", "C", "D", "E"];

function getDefaultChoices(): ChoiceInput[] {
  return [
    { label: "A", text: "", is_correct: false },
    { label: "B", text: "", is_correct: false },
    { label: "C", text: "", is_correct: false },
    { label: "D", text: "", is_correct: false },
  ];
}

function AdminNewQuestionPage() {
  const navigate = useNavigate();
  const { slug: examSlug } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  const [categories, setCategories] = useState<Category[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [questionNumber, setQuestionNumber] = useState("");
  const [textKo, setTextKo] = useState("");
  const [textEn, setTextEn] = useState("");
  const [categoryIdVal, setCategoryIdVal] = useState<string>("");
  const [isMultiple, setIsMultiple] = useState(false);
  const [choices, setChoices] = useState<ChoiceInput[]>(getDefaultChoices());
  const [explanation, setExplanation] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewKo, setPreviewKo] = useState(false);
  const [previewEn, setPreviewEn] = useState(false);
  const [previewExplanation, setPreviewExplanation] = useState(false);

  // Admin guard
  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [user, authLoading, navigate]);

  // Fetch categories
  useEffect(() => {
    if (!examSlug) return;
    getCategories(examSlug)
      .then(setCategories)
      .catch(() => {});
  }, [examSlug]);

  function updateChoice(index: number, field: keyof ChoiceInput, value: string | boolean) {
    setChoices((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  }

  function addChoice() {
    if (choices.length >= 5) return;
    const nextLabel = LABELS[choices.length];
    setChoices((prev) => [...prev, { label: nextLabel, text: "", is_correct: false }]);
  }

  function removeChoice() {
    if (choices.length <= 2) return;
    setChoices((prev) => prev.slice(0, -1));
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const url = await uploadImageToS3(file);
      setImageUrl(url);
    } catch {
      setError("이미지 업로드에 실패했습니다");
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!questionNumber || !textKo) {
      setError("문제 번호와 한국어 텍스트는 필수입니다");
      return;
    }

    const hasCorrect = choices.some((c) => c.is_correct);
    if (!hasCorrect) {
      setError("정답을 최소 1개 이상 선택해 주세요");
      return;
    }

    const emptyChoice = choices.some((c) => !c.text.trim());
    if (emptyChoice) {
      setError("모든 선택지의 텍스트를 입력해 주세요");
      return;
    }

    setSubmitting(true);

    try {
      const body: Record<string, unknown> = {
        question_number: Number(questionNumber),
        text_ko: textKo,
        is_multiple: isMultiple,
        choices: choices.map((c) => ({
          label: c.label,
          text: c.text,
          is_correct: c.is_correct,
        })),
      };
      if (textEn.trim()) body.text_en = textEn;
      if (categoryIdVal) body.category_id = Number(categoryIdVal);
      if (explanation.trim()) body.explanation = explanation;
      if (imageUrl) body.image_url = imageUrl;

      const res = await adminFetch(`/admin/exams/${examSlug}/questions`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "문제 등록에 실패했습니다");
      }

      navigate({
        to: "/exams/$slug/admin/questions",
        params: { slug: examSlug },
      });
    } catch (err) {
      setError(createErrorMessage(err, "문제 등록에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading || (!user || user.role !== "admin")) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/exams/$slug/admin/questions"
            params={{ slug: examSlug }}
            className="mb-4 inline-flex items-center text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t("admin.back_to_list")}
          </Link>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
            {t("admin.new_title")}
          </h1>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t("admin.basic_info")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="questionNumber">{t("admin.question_number")}</Label>
                  <Input
                    id="questionNumber"
                    type="number"
                    placeholder={t("admin.number_placeholder")}
                    value={questionNumber}
                    onChange={(e) => setQuestionNumber(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">{t("common.category")}</Label>
                  <Select
                    value={categoryIdVal || "none"}
                    onValueChange={(v) => setCategoryIdVal(!v || v === "none" ? "" : v)}
                  >
                    <SelectTrigger id="category" className="w-full">
                      <SelectValue placeholder={t("admin.select_category_option")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("admin.no_category")}</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="textKo">{t("admin.text_ko_label")}</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setPreviewKo((v) => !v)}
                  >
                    {previewKo ? <Pencil className="mr-1 h-3 w-3" /> : <Eye className="mr-1 h-3 w-3" />}
                    {previewKo ? t("admin.edit_tab") : t("admin.preview_tab")}
                  </Button>
                </div>
                {previewKo ? (
                  <div className="min-h-[100px] rounded-lg border border-input bg-muted/30 px-3 py-2">
                    {textKo ? <Markdown>{textKo}</Markdown> : <span className="text-sm text-muted-foreground">{t("admin.text_placeholder")}</span>}
                  </div>
                ) : (
                  <textarea
                    id="textKo"
                    rows={4}
                    placeholder={t("admin.text_ko_placeholder")}
                    value={textKo}
                    onChange={(e) => setTextKo(e.target.value)}
                    required
                    className="w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-2 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 md:text-sm dark:bg-input/30"
                  />
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="textEn">{t("admin.text_en_label")}</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setPreviewEn((v) => !v)}
                  >
                    {previewEn ? <Pencil className="mr-1 h-3 w-3" /> : <Eye className="mr-1 h-3 w-3" />}
                    {previewEn ? t("admin.edit_tab") : t("admin.preview_tab")}
                  </Button>
                </div>
                {previewEn ? (
                  <div className="min-h-[80px] rounded-lg border border-input bg-muted/30 px-3 py-2">
                    {textEn ? <Markdown>{textEn}</Markdown> : <span className="text-sm text-muted-foreground">{t("admin.text_placeholder")}</span>}
                  </div>
                ) : (
                  <textarea
                    id="textEn"
                    rows={3}
                    placeholder="English question text (optional)..."
                    value={textEn}
                    onChange={(e) => setTextEn(e.target.value)}
                    className="w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-2 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 md:text-sm dark:bg-input/30"
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="isMultiple"
                  type="checkbox"
                  checked={isMultiple}
                  onChange={(e) => setIsMultiple(e.target.checked)}
                  className="h-4 w-4 rounded border-zinc-300 text-primary accent-primary"
                />
                <Label htmlFor="isMultiple">{t("admin.is_multiple_label")}</Label>
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label>{t("admin.image_label")}</Label>
                {imageUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={imageUrl}
                      alt="문제 이미지"
                      className="max-h-48 rounded-lg border border-zinc-200 dark:border-zinc-700"
                    />
                    <button
                      type="button"
                      onClick={() => setImageUrl("")}
                      className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-zinc-300 p-6 text-sm text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-600 dark:border-zinc-600 dark:text-zinc-400 dark:hover:border-zinc-500">
                    {uploading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {t("common.uploading")}
                      </>
                    ) : (
                      <>
                        <ImagePlus className="h-5 w-5" />
                        {t("admin.image_upload_hint")}
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Choices */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("common.choices")}</CardTitle>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={removeChoice}
                    disabled={choices.length <= 2}
                  >
                    <Minus className="mr-1 h-3 w-3" />
                    {t("common.delete")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addChoice}
                    disabled={choices.length >= 5}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    {t("common.add")}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {choices.map((choice, idx) => (
                <div
                  key={choice.label}
                  className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-3 dark:border-zinc-700 sm:flex-row sm:items-center"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-sm font-bold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                    {choice.label}
                  </span>
                  <div className="flex-1">
                    <Input
                      placeholder={`${t("admin.choice_placeholder")} ${choice.label}`}
                      value={choice.text}
                      onChange={(e) => updateChoice(idx, "text", e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={choice.is_correct}
                      onChange={(e) => updateChoice(idx, "is_correct", e.target.checked)}
                      className="h-4 w-4 rounded border-zinc-300 text-primary accent-primary"
                      id={`correct-${choice.label}`}
                    />
                    <Label htmlFor={`correct-${choice.label}`} className="text-sm whitespace-nowrap">
                      {t("common.correct")}
                    </Label>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Explanation */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.explanation_label")}</CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setPreviewExplanation((v) => !v)}
                >
                  {previewExplanation ? <Pencil className="mr-1 h-3 w-3" /> : <Eye className="mr-1 h-3 w-3" />}
                  {previewExplanation ? t("admin.edit_tab") : t("admin.preview_tab")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {previewExplanation ? (
                <div className="min-h-[100px] rounded-lg border border-input bg-muted/30 px-3 py-2">
                  {explanation ? <Markdown>{explanation}</Markdown> : <span className="text-sm text-muted-foreground">{t("admin.explanation_placeholder")}</span>}
                </div>
              ) : (
                <textarea
                  rows={4}
                  placeholder={t("admin.explanation_placeholder")}
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  className="w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-2 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 md:text-sm dark:bg-input/30"
                />
              )}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Link
              to="/exams/$slug/admin/questions"
              params={{ slug: examSlug }}
              className="sm:order-1"
            >
              <Button type="button" variant="outline" className="w-full sm:w-auto">
                {t("common.cancel")}
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={submitting}
              className="sm:order-2"
            >
              {submitting ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-4 w-4" />
              )}
              {t("admin.create_question")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
